import { z } from 'zod';

const AccountTypeSchema = z.object({
  code: z.string(),
  displayString: z.string(),
  corporateSavings: z.boolean(),
  checking: z.boolean(),
  savings: z.boolean(),
  corporate: z.boolean(),
});

const PaymentTypeSchema = z.object({
  name: z.string(),
  displayString: z.string(),
});

const ECheckSchema = z
  .object({
    accountType: AccountTypeSchema,
    accountNumber: z.string(),
    routingNumber: z.string(),
    checkNumber: z.string().nullable(),
    bankName: z.string().nullable(),
    nameOnBankAccount: z.string().nullable(),
    token: z.string(),
    tokenized: z.boolean(),
  })
  .nullable();

const CreditCardSchema = z
  .object({
    cardType: z.object({
      code: z.string(),
      displayString: z.string(),
      masterCard: z.boolean(),
      visa: z.boolean(),
      diners: z.boolean(),
      amex: z.boolean(),
      discover: z.boolean(),
    }),
    cardNumber: z.string(),
    cardExpirationDate: z.string(),
    cardSecureCode: z.string().nullable(),
    cardHolderName: z.string().nullable(),
    validateCardSecureCode: z.boolean(),
    cardToken: z.string(),
    data: z.any().nullable(),
    tokenized: z.boolean(),
    expired: z.boolean(),
  })
  .nullable();

const StripeSchema = z
  .object({
    token: z.string(),
    type: z.string().optional(),
    cardType: z.string().optional(),
    cardLast4Digits: z.string().optional(),
    cardExpirationDate: z.string().optional(),
  })
  .nullable()
  .optional();

const PaymentMethodSchema = z.object({
  paymentType: PaymentTypeSchema,
  creditCard: CreditCardSchema,
  check: z.null(),
  echeck: ECheckSchema,
  payPal: z.null(),
  amazonPay: z.null(),
  origin: z.null(),
  stripe: StripeSchema,
});

const CustomerSchema = z.object({
  customerId: z.number(),
  userUuid: z.string().nullable(),
  username: z.string().nullable(),
  firstname: z.string(),
  lastname: z.string(),
  companyname: z.string(),
  address1: z.string(),
  address2: z.string(),
  city: z.string(),
  state: z.string(),
  country: z.string(),
  countrylong: z.string(),
  zipcode: z.string(),
  email: z.string(),
  contactEmail: z.string().nullable(),
  phone: z.string(),
  vatNumber: z.string().nullable(),
  billingType: z.string().nullable(),
  customerInSingleBilling: z.boolean(),
  billingLetterOption: z.string(),
  currencyCd: z.string(),
  billingPeriod: z.string().nullable(),
  billingDayOfMonth: z.number(),
  addressValidationRequired: z.boolean(),
  noPrepayAutoRenewOptin: z.number(),
  addressProvided: z.boolean(),
  singleBilledAccount: z.boolean(),
  partnerBilledAccount: z.boolean(),
});

const PartnerContactSchema = z.object({
  partner_contact_name: z.string(),
  partner_contact_email: z.string(),
});

const BillingInformationSchema = z.object({
  customer: CustomerSchema,
  payment_method: PaymentMethodSchema,
  partner_contact: PartnerContactSchema,
});

export const SingleBillResponseSchema = z.object({
  single_bill_enabled: z.boolean(),
  single_bill_activated: z.boolean(),
  account_balance: z.number().nullish(),
  account_balance_due: z.number().nullish(),
  next_billing_date: z.string().nullish(),
  currency_code: z.string().nullish(),
  locale: z.string().nullish(),
  paying_account_id: z.number().nullish(),
  billing_information: BillingInformationSchema.nullable(),
});

export type SingleBillResponse = z.infer<typeof SingleBillResponseSchema>;
